import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AthleteCard from "../components/AthleteCard";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1592919505780-303950717480";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [rising, setRising] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, r, rec] = await Promise.all([
          api.get("/athletes", { params: { featured: true, limit: 6 } }),
          api.get("/athletes", { params: { rising: true, limit: 4 } }),
          api.get("/athletes/recently-funded", { params: { limit: 4 } }),
        ]);
        setFeatured(f.data);
        setRising(r.data);
        setRecent(rec.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO, brand-aligned dark editorial */}
      <section className="relative overflow-hidden bg-foreground text-background" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Golf course" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent" />
        </div>
        <div className="container-tight relative pt-20 pb-20 md:pt-28 md:pb-32">
          <div className="overline text-background/70" data-testid="hero-overline">Talk Golf International · Golfer Fund · Est. 2026</div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight mt-8 max-w-4xl">
            Back the <span className="text-primary">next</span> generation<br />
            of <span className="">golf</span>.
          </h1>

          <div className="mt-10 grid md:grid-cols-2 gap-10 max-w-5xl">
            <p className="text-base md:text-lg text-background/75 leading-relaxed max-w-xl">
              A funding platform for golfers. Back the golfer you believe in, one-off or monthly, or sponsor them for
              a season. Direct support goes straight to the goals the golfer has listed on their profile.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end items-start">
              <Link to="/golfers" className="btn-sharp bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="hero-cta-support">
                Back a golfer <ArrowUpRight size={14} />
              </Link>
              <Link to="/apply" className="btn-sharp bg-transparent border border-background text-background hover:bg-background hover:text-foreground inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="hero-cta-apply">
                Apply for funding <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-16 md:mt-24 grid grid-cols-3 gap-0 border-t border-background/25 max-w-3xl">
            {[
              { k: "€480k+", v: "raised for golfers" },
              { k: "47", v: "verified golfers" },
              { k: "28", v: "corporate partners" },
            ].map((s, i) => (
              <div key={i} className="py-6 px-5 border-r border-background/25 last:border-r-0" data-testid={`hero-stat-${i}`}>
                <div className="font-brand text-4xl md:text-5xl">{s.k}</div>
                <div className="overline mt-2 text-background/60">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED ATHLETES, editorial mosaic */}
      <section className="py-20 md:py-28 border-b border-border" data-testid="featured-section">
        <div className="container-tight">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline">No. 01 · Featured golfers</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 max-w-2xl leading-[1.05]">
                The golfers <span className="text-primary">defining</span> Ireland's next golfing decade.
              </h2>
            </div>
            <Link to="/golfers" className="hidden md:inline-flex text-[12px] uppercase tracking-[0.18em] gap-1 items-center hover:text-primary" data-testid="featured-view-all">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>

          {featured.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {featured.slice(0, 6).map((a, i) => (
                <AthleteCard key={a.athlete_id} athlete={a} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* RISING STARS */}
      <section className="py-20 md:py-28 border-b border-border bg-muted" data-testid="rising-section">
        <div className="container-tight">
          <div className="overline">No. 02 · Rising stars</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 mb-12 max-w-2xl leading-[1.05]">
            Watch this <span className="text-primary">space</span>, talent in the making.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
            {rising.map((a, i) => <AthleteCard key={a.athlete_id} athlete={a} index={i} />)}
          </div>
        </div>
      </section>

      {/* RECENTLY FUNDED */}
      {recent.length > 0 && (
        <section className="py-20 md:py-28 border-b border-border" data-testid="recent-section">
          <div className="container-tight">
            <div className="overline">No. 03 · Recently funded</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 mb-12 max-w-2xl leading-[1.05]">
              Fresh support landing this week.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
              {recent.map((a, i) => <AthleteCard key={a.athlete_id} athlete={a} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS STRIP */}
      <section className="py-16 border-b border-border" data-testid="sponsor-spotlight">
        <div className="container-tight">
          <div className="overline text-center">In partnership with</div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
            {["Brand One", "Brand Two", "Brand Three", "Brand Four", "Brand Five"].map((b, i) => (
              <div
                key={b}
                className="font-display text-xl md:text-2xl text-center text-foreground/40 hover:text-foreground transition-colors"
                data-testid={`sponsor-logo-${i}`}
              >
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORY EDITORIAL */}
      <section className="py-20 md:py-32 border-b border-border bg-foreground text-background" data-testid="success-story">
        <div className="container-tight grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="aspect-[4/5] overflow-hidden border border-background/20">
              <img
                src="https://images.unsplash.com/photo-1633597468433-fdb200b73f62"
                alt="Golfer"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="overline text-background/60">No. 04 · Success story</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.1]">
              "TGI helped me fund a full season, and an <span className="text-secondary">LET card</span> followed."
            </h2>
            <p className="mt-6 text-background/70 leading-relaxed max-w-lg">
              From a club junior to a tour pro in three seasons. The TGI Golfer Fund covered the entry fees, travel and
              coaching that made the difference, backed by a community of supporters who believed early.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="font-display text-xl">Sarah K.</div>
              <div className="h-px flex-1 bg-background/30" />
              <div className="overline text-background/60">LET Pro · 2025</div>
            </div>
            <div className="mt-8">
              <Link to="/golfers" className="btn-sharp bg-transparent border border-background text-background hover:bg-background hover:text-foreground inline-flex items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="success-cta">
                Find an golfer to back <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground" data-testid="apply-cta-section">
        <div className="container-tight grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline text-primary-foreground/70">For golfers</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.02]">
              Are you the <span className="">next</span> name on this list?
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/apply" className="inline-flex btn-sharp bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/90 items-center gap-2 uppercase tracking-[0.18em] text-[12px]" data-testid="footer-apply-cta">
              Get listed for sponsorship <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
